import React, { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Mail, Send, Check } from "lucide-react";
import { useCruise } from "../context/CruiseContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Newsletter = () => {
  const { EVENT } = useCruise();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!valid) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, {
        name: name.trim(),
        phone: phone.trim(),
        email,
        cruise: EVENT.id,
      });
      setDone(true);
      toast.success("You're subscribed! Watch your inbox for early-bird alerts.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative bg-[#111111] py-16 border-t border-white/10">
      <div className="max-w-4xl mx-auto px-5 text-center">
        <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#E50914]/15 text-[#E50914] mb-5">
          <Mail size={26} />
        </span>
        <h2 className="font-display uppercase text-white text-3xl sm:text-5xl leading-[0.95]">
          Stay In The <span className="text-[#E50914]">Loop</span>
        </h2>
        <p className="mt-4 text-white/60 max-w-xl mx-auto">
          Join the mailing list for cruise updates, event announcements and{" "}
          <span className="text-[var(--hh-gold)]">early-bird cabin alerts</span> before they sell out.
        </p>

        {done ? (
          <div className="mt-8 inline-flex items-center gap-3 bg-[#1B8A5A]/15 border border-[#1B8A5A]/40 px-6 py-4">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#1B8A5A] text-white">
              <Check size={18} />
            </span>
            <span className="font-cond uppercase tracking-wide text-white">You're on the list — see you at sea!</span>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 max-w-lg mx-auto">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input
                type="text"
                data-testid="newsletter-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name *"
                required
                className="bg-[#0A0A0A] border-white/15 text-white rounded-lg h-14 px-4 focus-visible:ring-[#E50914]"
              />
              <Input
                type="tel"
                data-testid="newsletter-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number *"
                required
                className="bg-[#0A0A0A] border-white/15 text-white rounded-lg h-14 px-4 focus-visible:ring-[#E50914]"
              />
            </div>
            <Input
              type="email"
              data-testid="newsletter-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address *"
              required
              className="mt-3 w-full bg-[#0A0A0A] border-white/15 text-white rounded-lg h-14 px-4 focus-visible:ring-[#E50914]"
            />
            <Button
              type="submit"
              data-testid="newsletter-subscribe-btn"
              disabled={loading}
              className="mt-3 w-full bg-[#E50914] hover:bg-[#c20710] text-white font-cond uppercase tracking-widest rounded-full h-14 px-8"
            >
              <Send size={16} className="mr-2" /> {loading ? "Subscribing..." : "Subscribe"}
            </Button>
            <p className="mt-3 text-white/40 text-xs">All fields required. We'll only use these to send cruise updates.</p>
          </form>
        )}
      </div>
    </section>
  );
};

export default Newsletter;
